import React, { useState, useContext, useEffect } from 'react'
import CloseIcon from './close.svg'
import ArrowLeftIcon from './arrow-left.svg'
import ArrowRightIcon from './arrow-right.svg'
import CheckIcon from './check.svg'
import { NavInactiveDepartmentFragment, NavActiveDepartmentFragment } from '#src/generated/graphql'
import { Box, DepartmentIcon } from '@deal/components'
import { NavLink, Link } from 'react-router-dom'
import FlyoutMenu, { FlyoutMenuSectionHeader } from '#src/components/FlyoutMenu'
import { DepartmentContext } from '#src/context/department'
import { IdentityContext } from '#src/containers/Identity'
import ReleaseConsumerIdentityLink from '../ReleaseConsumerIdentityLink'
import classnames from 'classnames'
import styles from './styles.css'
import { Experiment, Variation } from '@deal/experiment-js'
import { generateReferAFriendUrl } from '#src/services/referrals'
import { AnalyticsContext } from '#src/containers/Analytics'

interface ExploreMenuProps {
  open: boolean
  onClose: () => void
  departments: NavInactiveDepartmentFragment[] | null
  activeDepartment?: NavActiveDepartmentFragment
  onFeedbackClicked: () => void
}

const ExploreMenu: React.FunctionComponent<ExploreMenuProps> = ({
  open,
  onClose,
  departments,
  onFeedbackClicked
}) => {
  const [userMenuExpanded, setUserMenuExpanded] = useState(false)
  const { myself } = useContext(IdentityContext)
  const { department: activeDepartment } = useContext(DepartmentContext)
  const analytics = useContext(AnalyticsContext)

  useEffect(() => {
    open &&
      myself &&
      analytics &&
      analytics.events.referralPageEntryPointViewed({
        entry_point: 'explore-menu'
      })
  }, [open])

  const isImpersonating = myself && myself.user.id !== myself.realUser.id

  return (
    <FlyoutMenu from="right" open={open} onClose={onClose} title="Menu">
      <ul className={styles.list}>
        {myself && (
          <li className={styles.listItemMobile}>
            <a className={styles.listItemLink} onClick={() => setUserMenuExpanded(true)}>
              <span className={styles.listItemLabel}>
                {myself.user.firstName}
                {isImpersonating && ` (${myself.realUser.firstName})`} <ArrowRightIcon />
              </span>
            </a>
          </li>
        )}
        {myself && myself.user.isAllowedToRefer && (
          <li className={styles.listItem}>
            <NavLink
              to={generateReferAFriendUrl({ source: 'explore-menu' })}
              className={styles.listItemLink}
              activeClassName={styles.listItemLinkActive}
            >
              <span className={styles.listItemLabel}>Get 10% off</span>
            </NavLink>
          </li>
        )}
        <li className={styles.listItem}>
          <NavLink
            to="/about-us"
            className={styles.listItemLink}
            activeClassName={styles.listItemLinkActive}
          >
            <span className={styles.listItemLabel}>About us</span>
          </NavLink>
        </li>
        <li className={styles.listItem}>
          <NavLink
            to="/team"
            className={styles.listItemLink}
            activeClassName={styles.listItemLinkActive}
          >
            <span className={styles.listItemLabel}>Meet the team</span>
          </NavLink>
        </li>
        <li className={styles.listItem}>
          <NavLink
            to="/support"
            className={styles.listItemLink}
            activeClassName={styles.listItemLinkActive}
          >
            <span className={styles.listItemLabel}>Help</span>
          </NavLink>
        </li>
      </ul>

      {departments && departments.length > 0 && (
        <>
          <FlyoutMenuSectionHeader>Explore categories</FlyoutMenuSectionHeader>
          <ul className={styles.list}>
            {departments.map(department => (
              <li className={styles.listItem} key={department.id}>
                <Link
                  to={`/d/${department.slug}`}
                  onClick={onClose}
                  className={classnames(styles.listItemLink, {
                    [styles.listItemLinkActive]:
                      activeDepartment && activeDepartment.id === department.id
                  })}
                >
                  <DepartmentIcon
                    departmentSlug={department.slug}
                    className={styles.listItemIcon}
                  />
                  <span className={styles.listItemLabel}>{department.displayName}</span>
                  <CheckIcon className={styles.listItemCheckIcon} />
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {myself && (
        <Box
          column
          spacing="none"
          className={classnames(styles.userMenu, {
            [styles.userMenuExpanded]: userMenuExpanded
          })}
          props={{
            onClick: e => e.stopPropagation()
          }}
        >
          <FlyoutMenuSectionHeader onClick={() => setUserMenuExpanded(false)}>
            <div className={styles.back}>
              <ArrowLeftIcon className={styles.backIcon} />
              {myself.user.firstName}
            </div>
            <CloseIcon className={styles.closeIcon} onClick={onClose} />
          </FlyoutMenuSectionHeader>

          {/* Note: This menu is mobile-only. If we change these links, make sure to also change the desktop menu. */}
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <NavLink
                to="/account"
                className={styles.listItemLink}
                activeClassName={styles.listItemLinkActive}
              >
                <span className={styles.listItemLabel}>My account</span>
              </NavLink>
            </li>
            <li className={styles.listItem}>
              <NavLink
                to="/account/orders"
                className={styles.listItemLink}
                activeClassName={styles.listItemLinkActive}
              >
                <span className={styles.listItemLabel}>My orders</span>
              </NavLink>
            </li>
            <Experiment id="curations-in-nav" default="off">
              <Variation id="on">
                <li className={styles.listItem}>
                  <NavLink
                    to="/curations"
                    className={styles.listItemLink}
                    activeClassName={styles.userMenuListItemLinkActive}
                  >
                    <span> My curations</span>
                  </NavLink>
                </li>
              </Variation>

              <Variation id="off">
                <li className={styles.listItem}>
                  <NavLink
                    to="/favorites"
                    className={styles.listItemLink}
                    activeClassName={styles.userMenuListItemLinkActive}
                  >
                    <span> My favorites</span>
                  </NavLink>
                </li>
              </Variation>
            </Experiment>
            <Experiment id="feedback-popup" default="off">
              <Variation id="on">
                <li className={styles.listItem}>
                  <div
                    className={styles.listItemLink}
                    onClick={() => {
                      onFeedbackClicked()
                      setUserMenuExpanded(false)
                      onClose()
                    }}
                  >
                    <span className={styles.listItemLabel}>Give feedback</span>
                  </div>
                </li>
              </Variation>
              <Variation id="off" />
            </Experiment>
            <li className={styles.listItem}>
              <NavLink
                to="/support/order-support"
                className={styles.listItemLink}
                activeClassName={styles.listItemLinkActive}
              >
                <span className={styles.listItemLabel}>Customer Support</span>
              </NavLink>
            </li>
            <li className={styles.listItem}>
              <NavLink
                to="/support/faq"
                className={styles.listItemLink}
                activeClassName={styles.listItemLinkActive}
              >
                <span className={styles.listItemLabel}>FAQ</span>
              </NavLink>
            </li>
            {isImpersonating && (
              <li className={styles.listItem}>
                <ReleaseConsumerIdentityLink className={styles.listItemLink}>
                  <span className={styles.listItemLabel}>
                    Back to "{myself.realUser.firstName}"
                  </span>
                </ReleaseConsumerIdentityLink>
              </li>
            )}
            <li className={styles.listItem}>
              <NavLink
                to="/auth/logout"
                className={styles.listItemLink}
                activeClassName={styles.listItemLinkActive}
              >
                <span className={styles.listItemLabel}>Sign out</span>
              </NavLink>
            </li>
          </ul>
        </Box>
      )}
    </FlyoutMenu>
  )
}

export default ExploreMenu
